/**
 * Where one inheritable field's value comes from, beside it: "from binom"
 * (the campaign group's master config — editing the field makes it this
 * container's own), or, once changed here, "Own value" with a real button
 * back: "Use binom's · 500 SMS" (user, 12/10/2026: going back must be
 * obvious). In the master config's own editor: who keeps their own value,
 * and "Override N" (OwnersTag, 21/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Crown, PencilLine, Undo2 } from 'lucide-react';
import type { DropGenMasterSummary, DropGenSection } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui';

import { OwnersTag } from './push/markers/OwnersTag';
import { useSectionSources } from './section-sources';

interface Props {
  section: DropGenSection;
  /** The master config's value in words, shown on the way back ("500 SMS"). */
  masterValue?: (master: DropGenMasterSummary) => string;
}

export const InheritTag: FC<Props> = ({ section, masterValue }) => {
  const sources = useSectionSources();
  if (!sources.following) return <OwnersTag section={section} />;
  const name = sources.masterRef?.name ?? 'the master config';
  if (sources.isOwn(section)) {
    const theirs = sources.masterRef && masterValue ? masterValue(sources.masterRef) : null;
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        <span className="inline-flex items-center gap-1 text-[12px] font-medium text-warning">
          <PencilLine className="h-3 w-3" aria-hidden /> Own value
        </span>
        <button
          type="button"
          onClick={() => sources.setOwn(section, false)}
          title={`Drop this container's own value and follow ${name}'s master config again`}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-[12px] font-medium text-foreground/80 shadow-sm transition-colors duration-200 hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
        >
          <Undo2 className="h-3 w-3" aria-hidden /> Use {name}&apos;s{theirs ? ` · ${theirs}` : ''}
        </button>
      </span>
    );
  }
  return (
    <Tooltip content={`From the ${name} master config — change it here to make it this container's own`}>
      <span className="inline-flex h-8 cursor-default items-center gap-1.5 whitespace-nowrap px-2 text-[12px] text-muted-foreground">
        <Crown className="h-3 w-3" /> from {name}
      </span>
    </Tooltip>
  );
};
